/**
 * ============================================================================
 * [P3-01-G] TextDetailModal - 글 상세 보기/편집 모달
 *
 * 역할:
 * - 전체 내용 표시
 * - 인라인 편집 (제목, 주제, 내용)
 * - 삭제 확인
 *
 * 접근성:
 * - ESC 키로 모달 닫기
 * - 바깥 영역 클릭으로 닫기
 * - aria-modal, role="dialog" 속성
 * ============================================================================
 */

import { useState, useCallback, useEffect, useRef } from 'react'
import { formatRelativeTime } from '@/utils'
import { useAppStore } from '@/stores'
import type { SavedText } from '@/types'
import styles from './Saved.module.css'

// ============================================================================
// Props 타입 정의
// ============================================================================
interface TextDetailModalProps {
  text: SavedText
  onClose: () => void
  onEdit: (id: string, updates: Partial<SavedText>) => Promise<boolean>
  onDelete: (id: string) => Promise<boolean>
}

// ============================================================================
// Component
// ============================================================================
export function TextDetailModal({
  text,
  onClose,
  onEdit,
  onDelete,
}: TextDetailModalProps) {
  // ========================================================================
  // Store 액션
  // ========================================================================
  const loadSavedTextToEditor = useAppStore((s) => s.loadSavedTextToEditor)
  const showToast = useAppStore((s) => s.showToast)

  // ========================================================================
  // 편집 모드 상태
  // ========================================================================
  const [isEditing, setIsEditing] = useState(false)
  const [editedTitle, setEditedTitle] = useState(text.title)
  const [editedTopic, setEditedTopic] = useState(text.topic || '')
  const [editedContent, setEditedContent] = useState(text.content)
  const [isSaving, setIsSaving] = useState(false)

  // ========================================================================
  // Ref for focus management
  // ========================================================================
  const modalRef = useRef<HTMLDivElement>(null)
  const titleInputRef = useRef<HTMLInputElement>(null)

  // ========================================================================
  // 편집 취소 핸들러
  // - ESC 키 핸들러에서 사용하기 위해 먼저 선언
  // ========================================================================
  const handleCancelEdit = useCallback(() => {
    // 원본 데이터로 복원
    setEditedTitle(text.title)
    setEditedTopic(text.topic || '')
    setEditedContent(text.content)
    setIsEditing(false)
  }, [text.title, text.topic, text.content])

  // ========================================================================
  // ESC 키 핸들러
  // CRITICAL: 모달이 열려있을 때만 ESC 키 감지
  // ========================================================================
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (isEditing) {
          // 편집 중이면 편집 취소
          handleCancelEdit()
        } else {
          // 아니면 모달 닫기
          onClose()
        }
      }
    }
    document.addEventListener('keydown', handleEsc)
    return () => document.removeEventListener('keydown', handleEsc)
  }, [onClose, isEditing, handleCancelEdit])

  // ========================================================================
  // 편집 모드 시작 시 제목 입력에 포커스
  // ========================================================================
  useEffect(() => {
    if (isEditing && titleInputRef.current) {
      titleInputRef.current.focus()
    }
  }, [isEditing])

  // ========================================================================
  // 바깥 영역 클릭 핸들러
  // ========================================================================
  const handleBackdropClick = useCallback(
    (e: React.MouseEvent) => {
      if (e.target === e.currentTarget) {
        if (isEditing) {
          // 편집 중이면 확인 요청
          if (window.confirm('변경 사항을 저장하지 않고 닫으시겠습니까?')) {
            onClose()
          }
        } else {
          onClose()
        }
      }
    },
    [onClose, isEditing]
  )

  // ========================================================================
  // 저장 핸들러
  // ========================================================================
  const handleSave = useCallback(async () => {
    setIsSaving(true)
    try {
      const success = await onEdit(text.id, {
        title: editedTitle,
        topic: editedTopic,
        content: editedContent,
      })
      if (success) {
        setIsEditing(false)
      }
    } catch (err) {
      console.error('[TextDetailModal] 저장 실패:', err)
    } finally {
      setIsSaving(false)
    }
  }, [text.id, editedTitle, editedTopic, editedContent, onEdit])

  // ========================================================================
  // 삭제 핸들러
  // ========================================================================
  const handleDelete = useCallback(async () => {
    if (window.confirm('정말 삭제하시겠습니까?\n이 작업은 되돌릴 수 없습니다.')) {
      const success = await onDelete(text.id)
      if (success) {
        onClose()
      }
    }
  }, [text.id, onDelete, onClose])

  // ========================================================================
  // 편집기로 불러오기 핸들러
  // ========================================================================
  const handleLoadToEditor = useCallback(() => {
    loadSavedTextToEditor(text)
    const label = text.type === 'reference' ? '레퍼런스' : '작성 글'
    showToast(`${label}을 편집 영역으로 불러왔습니다.`, 'success')
    onClose()
  }, [text, loadSavedTextToEditor, showToast, onClose])

  // ========================================================================
  // 날짜 포맷팅
  // ========================================================================
  const formattedDate = formatRelativeTime(
    text.createdAt instanceof Date ? text.createdAt : new Date(text.createdAt)
  )

  // ========================================================================
  // Render
  // ========================================================================
  return (
    <div
      className={styles.modalBackdrop}
      onClick={handleBackdropClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby="modal-title"
    >
      <div className={styles.modal} ref={modalRef}>
        {/* ================================================================
            헤더: 제목 (편집 가능)
            ================================================================ */}
        <div className={styles.modalHeader}>
          {isEditing ? (
            <input
              ref={titleInputRef}
              type="text"
              className={styles.editInput}
              value={editedTitle}
              onChange={(e) => setEditedTitle(e.target.value)}
              placeholder="제목을 입력하세요"
              aria-label="제목"
            />
          ) : (
            <h2 id="modal-title">{text.title || '제목 없음'}</h2>
          )}
          <button
            className={styles.closeBtn}
            onClick={onClose}
            aria-label="닫기"
          >
            ×
          </button>
        </div>

        {/* ================================================================
            메타 정보: 날짜, 주제, 타입, 글자수
            ================================================================ */}
        <div className={styles.modalMeta}>
          {isEditing ? (
            <>
              <span>주제:</span>
              <input
                type="text"
                className={styles.editInput}
                value={editedTopic}
                onChange={(e) => setEditedTopic(e.target.value)}
                placeholder="주제 (선택사항)"
                aria-label="주제"
                style={{ maxWidth: '200px' }}
              />
            </>
          ) : (
            <>
              <span>{formattedDate}</span>
              {text.topic && <span>#{text.topic}</span>}
              <span>{text.type}</span>
              <span>{text.content.length}자</span>
            </>
          )}
        </div>

        {/* ================================================================
            본문 (편집 가능)
            ================================================================ */}
        <div className={styles.modalContent}>
          {isEditing ? (
            <>
              <textarea
                className={styles.editTextarea}
                value={editedContent}
                onChange={(e) => setEditedContent(e.target.value)}
                placeholder="내용을 입력하세요"
                aria-label="내용"
              />
              <div style={{ marginTop: '8px', textAlign: 'right', color: '#888' }}>
                {editedContent.length}자
              </div>
            </>
          ) : (
            <p style={{ whiteSpace: 'pre-wrap', margin: 0 }}>{text.content}</p>
          )}
        </div>

        {/* ================================================================
            액션 버튼
            ================================================================ */}
        <div className={styles.modalActions}>
          {isEditing ? (
            <>
              <button
                className={styles.cancelBtn}
                onClick={handleCancelEdit}
                disabled={isSaving}
              >
                취소
              </button>
              <button
                className={styles.saveBtn}
                onClick={handleSave}
                disabled={isSaving}
              >
                {isSaving ? '저장 중...' : '저장'}
              </button>
            </>
          ) : (
            <>
              <button
                className={styles.deleteBtn}
                onClick={handleDelete}
              >
                삭제
              </button>
              <button
                className={styles.editBtn}
                onClick={() => setIsEditing(true)}
              >
                편집
              </button>
              <button
                className={styles.loadToEditorBtn}
                onClick={handleLoadToEditor}
              >
                📝 편집기로 불러오기
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  )
}

export default TextDetailModal
